/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import ActionModel from 'cio/models/action';
import { VWBContext } from 'cio/utils/visual-workflow-builder/context';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import isDestroyed from 'cio/utils/is-destroyed';
import EventBusService, {
  CONFIRM_TRANSITION,
  VWB_INITIATE_SINGLE_DELETE_FROM_KEYBOARD_SHORTCUT,
} from 'cio/services/event-bus';
import { elementContainsTarget } from 'cio/utils/dom';
import { isEventTargetInWormhole } from 'cio/utils/wormhole';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import isMultiLangBranchChild from 'cio/utils/visual-workflow-builder/is-multi-lang-branch-child';
import { isNone } from '@ember/utils';
import AuthService from 'cio/services/auth';
import DS from 'ember-data';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import CampaignModel from 'cio/models/campaign';
import Transition from '@ember/routing/-private/transition';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import AlertsService from 'cio/services/alerts';
import { task } from 'ember-concurrency';
import AccountService from 'cio/services/account';
import VWBEditActionSidebarDelay from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/delay';
import VWBEditActionSidebarTimeWindow from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/time-window';
import VWBEditActionSidebarWaitUntil from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/wait-until';
import VWBEditActionSidebarMessage from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/message';
import VWBEditActionSidebarCreateEvent from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/create-event';
import VWBEditActionSidebarCreateOrUpdatePerson from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/create-or-update-person';
import VWBEditActionSidebarBatchUpdate from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/batch-update';
import VWBEditActionSidebarManualSegmentUpdate from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/manual-segment-update';
import VWBEditActionSidebarSplit from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/split';
import VWBEditActionSidebarMultiSplit from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/multi-split';
import VWBEditActionSidebarConditionalSplit from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/conditional-split';
import VWBEditActionSidebarRandomCohortSplit from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/random-cohort-split';
import VWBEditActionSidebarExit from 'cio/components/visual-workflow-builder-v2/edit-action-sidebar/exit';

interface EditActionSidebarArgs {
  actionAbstract: any;
  campaign: CampaignModel;
  onSelect: (action: ActionModel) => unknown;
  onDeselect: () => unknown;
  onDeleteConfirmed: () => unknown;
  startBulkDelete: () => unknown;
  duplicateAction: () => unknown;
  vwbContext: VWBContext;
  isAddingOrMoving: boolean;
  tab: VWBTab;
  demoMode: boolean;
  hasReadOnlyPermission: boolean;
}

export default class EditActionSidebar extends Component<EditActionSidebarArgs> {
  @service apiErrors!: ApiErrorsService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service eventBus!: EventBusService;
  @service store!: DS.Store;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;
  @service environment!: EnvironmentService;
  @service alerts!: AlertsService;
  @service account!: AccountService;

  @tracked actionAbstract: Record<any, any> = {};
  clickStartedWithin = false;
  container: HTMLElement | null = null;
  transition: Transition | null = null;
  @tracked showDiscardModal = false;
  @tracked hasCompletedSetup = true;

  get actionName() {
    return this.args.actionAbstract.actionType.replace(/_action$/, '');
  }

  get isRedactedUserEditingWebhook() {
    return this.auth.redacted && this.actionName === 'webhook';
  }

  get sidebarTitle() {
    if (this.actionAbstract == null) {
      return;
    }
    let { info } = this.actionAbstract.action;
    if (info.editSidebarName) {
      return info.editSidebarName;
    }
    let name = this.hasCompletedSetup ? info.name : this.actionName;
    name = name === 'Twilio SMS' ? name : String(name).toLowerCase();
    return `${this.hasCompletedSetup ? 'Edit ' : 'Connect with '} ${name}`;
  }

  get actionComponent() {
    if (isNone(this.actionAbstract)) {
      return;
    }

    return {
      delay: VWBEditActionSidebarDelay,
      time_window: VWBEditActionSidebarTimeWindow,
      wait_until: VWBEditActionSidebarWaitUntil,
      message: VWBEditActionSidebarMessage,
      create_event: VWBEditActionSidebarCreateEvent,
      create_or_update_person: VWBEditActionSidebarCreateOrUpdatePerson,
      batch_update: VWBEditActionSidebarBatchUpdate,
      manual_segment_update: VWBEditActionSidebarManualSegmentUpdate,
      split: VWBEditActionSidebarSplit,
      multi_split: VWBEditActionSidebarMultiSplit,
      conditional_split: VWBEditActionSidebarConditionalSplit,
      random_cohort_split: VWBEditActionSidebarRandomCohortSplit,
      exit: VWBEditActionSidebarExit,
    }[this.actionAbstract?.type as string]!;
  }

  get workflowPath() {
    let prefix = this.args.campaign.isApiTriggered ? 'broadcasts.broadcast' : 'campaigns.campaign';

    return `${prefix}.${this.args.campaign.isDraft ? 'setup' : 'overview'}.workflow.actions`;
  }

  get showIDLink() {
    return !this.args.demoMode;
  }

  @action
  checkIfActionNeedsSetup() {
    if (this.args.actionAbstract.action.info.requiresSetup) {
      this.loadEnvironment.perform();
    }
  }

  loadEnvironment = task(async () => {
    try {
      await this.environment.current.reload();
      this.hasCompletedSetup = this.environment.current.enabledActions.includes(this.actionName);
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  constructor(owner: unknown, args: EditActionSidebarArgs) {
    super(owner, args);
    this.setLocalActionAbstract();
    this.checkIfActionNeedsSetup();
  }

  @action
  onUpdate() {
    // Need to reassign the this.args.actionAbstract from outside to an internal property
    // to support sidebar transition animations well.
    // If we don't do this assignment, the sidebar may become empty while
    // transitioning, due to a deselect. This essentially "caches" the value for
    // hiding the sidebar.
    // TODO: Creating a split will modify the exiting sidebar in place into the
    // target split's sidebar, but also transition in a new sidebar.
    // Ideally, the exiting sidebar would remain the old action's sidebar.
    // The problem here is that creating a split changes the current action into
    // a split type, so the underlying action model doesn't change, only its
    // type does. Still need to figure out how to best "cache" the old action
    // here.
    this.setLocalActionAbstract();
  }

  @action
  private setLocalActionAbstract() {
    if (!isNone(this.args.actionAbstract)) {
      this.actionAbstract = this.args.actionAbstract;
    }
  }

  get isMultiLangBranchChildAction() {
    return isMultiLangBranchChild(this.args.actionAbstract.action);
  }

  willDestroy() {
    super.willDestroy();
    this.removeHandlers();
  }

  @action
  didInsert(element: HTMLElement) {
    this.container = element;
    this.addHandlers();
  }

  @action
  addHandlers() {
    this.container?.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('click', this.onOutsideClick, true);
    document.addEventListener('keyup', this.onKeyUp);

    this.eventBus.subscribe(CONFIRM_TRANSITION, this, this.confirmTransition);
  }

  @action
  removeHandlers() {
    this.container?.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('click', this.onOutsideClick, true);
    document.removeEventListener('keyup', this.onKeyUp);

    this.eventBus.unsubscribe(CONFIRM_TRANSITION, this, this.confirmTransition);
  }

  @action
  onKeyUp(event: FlyKeyboardEvent<HTMLElement>) {
    // Show the delete modal if the Delete or Backspace is pressed.
    // Ignore the delete key if we're within an input (as that means we're
    // typing something).
    // Since the delete flow relies on the CioDeleteButton, we publish an event
    // to it to initiate the delete.
    // TODO: Refactor edit sidebars to make deleting more consistent, both
    // between the edit sidebars and with the multi-select delete modal.
    if (
      (event.key === 'Delete' || event.key === 'Backspace') &&
      !['INPUT', 'TEXTAREA'].includes(event.target.tagName) &&
      !this.args.hasReadOnlyPermission &&
      (this.args.actionAbstract.canDelete ?? true) &&
      (!this.args.actionAbstract.saveTask?.isRunning ?? true) &&
      (!this.args.actionAbstract.deleteTask?.isRunning ?? true)
    ) {
      this.eventBus.publish(VWB_INITIATE_SINGLE_DELETE_FROM_KEYBOARD_SHORTCUT);
    }

    if (event.key === 'Escape') {
      this.maybeCloseSidebar();
    }
  }

  @action
  onMouseDown() {
    this.clickStartedWithin = true;
  }

  @action
  confirmTransition(transition: Transition) {
    if (this.actionAbstract.isDirty) {
      this.transition = transition;
      this.showDiscardModal = true;
    }
  }

  @action
  onOutsideClick(event: MouseEvent) {
    if (event.defaultPrevented || this.clickStartedWithin) {
      this.clickStartedWithin = false;
      return;
    }
    this.clickStartedWithin = false;

    // Don't hide sidebar on outside clicks if the edited action is an exit.
    // This way we can still click-to-scroll around the canvas.
    if (this.args.actionAbstract.action.isExit) {
      return;
    }

    if (!isDestroyed(this)) {
      let rootElement = this.container;
      let isComponent = rootElement === event.target;
      let inComponent = elementContainsTarget(rootElement, event.target);

      let inCurrentAction = elementContainsTarget(
        document.querySelector(`.js-vwb-workflow-action[data-id="${this.args.actionAbstract.action.id}"]`),
        event.target,
      );
      let inWormhole = isEventTargetInWormhole(event);

      if (isComponent || inComponent || inCurrentAction || inWormhole) {
        return;
      }

      this.maybeCloseSidebar();
    }
  }

  @action
  maybeCloseSidebar() {
    if (this.actionAbstract?.isDirty) {
      this.showDiscardModal = true;
    } else {
      this.args.onDeselect();
    }
  }

  @action
  closeDiscardModal() {
    this.showDiscardModal = false;
    this.transition = null;
  }

  @action
  onEditParentTest() {
    let parent = this.actionAbstract.action.belongsTo('parentAction').value();
    this.args.onSelect(parent);
  }

  @action
  onCancel() {
    this.maybeCloseSidebar();
  }

  @action
  onCloseDiscardModal() {
    this.closeDiscardModal();
  }

  @action
  onDiscard() {
    this.showDiscardModal = false;

    // For A/B tests, there is a different category of events, so we want to skip this one.
    if (!this.args.actionAbstract.splitAllocationsChanged) {
      this.analytics.track('campaign', 'edit_discarded', {
        model: this.args.campaign,
        action: this.args.actionAbstract.action,
        from: 'workflow',
      });
    }

    this.args.onDeselect();

    let { transition } = this;
    if (!isNone(transition)) {
      transition?.retry();
    }
  }

  // This is a serialized version of the action that's going to be deleted.
  // This is so that we can safely call the delete_completed event after a
  // delete, even if the model is gone.
  // Another example is: when "deleting" a split, the split action gets turned
  // into one if its variations, and that's what would be used in the
  // "completed" event, which would be incorrect.
  deleteActionCopy = null;

  deleteInitiationType = 'edit sidebar';

  @action
  onDeleteStarted(options = { fromDeleteEvent: false }) {
    this.deleteActionCopy = this.args.actionAbstract.action.serialize({ includeId: true });

    this.deleteInitiationType = options?.fromDeleteEvent ? 'keyboard' : 'edit sidebar';

    this.analytics.track('campaign', 'workflow_existing_item_delete_started', {
      model: this.args.campaign,
      actions: [this.args.actionAbstract.action],
      trayTab: this.args.tab,
      initiationType: this.deleteInitiationType,
    });
  }

  @action
  onDeleteConfirmed() {
    if (this.args.onDeleteConfirmed) {
      this.args.onDeleteConfirmed();
    }

    this.analytics.track('campaign', 'workflow_existing_item_delete_confirmed', {
      model: this.args.campaign,
      actions: [this.deleteActionCopy],
      trayTab: this.args.tab,
      initiationType: this.deleteInitiationType,
    });
  }

  @action
  onSetupComplete() {
    this.hasCompletedSetup = true;
  }

  get isCollection() {
    return this.args.actionAbstract.actionType === 'collection_query_action';
  }

  get blockDuplicateCollection() {
    return !this.account.planFeatures.collections.enabled;
  }

  get duplicateDisabled() {
    return (
      this.isRedactedUserEditingWebhook ||
      this.args.hasReadOnlyPermission ||
      this.actionAbstract.isDirty ||
      this.args.isAddingOrMoving ||
      (this.blockDuplicateCollection && this.isCollection) ||
      // actionAbstract isn't typed correctly, so the linter can't determine
      // whether booleans exist. Disable the lint rule here until all
      // ActionAbstract classes are typed correctly
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      (this.args.actionAbstract.action.isBranch && !this.args.actionAbstract.canDelete)
    );
  }
}
